/**
 * A/B test utilities for the Hero NaaS Configurator CTA.
 *
 * Bucketing is deterministic per browser: a UUID is generated once and stored
 * in localStorage["ab_cta_variant"]. This gives a stable 1/3 split without
 * any server-side infrastructure.
 *
 * Future: replace getCTAVariant() with a server-side lookup via Edge Config
 * by setting EDGE_CONFIG in .env (see .env.example).
 */

import { trackEvent } from "@/lib/analytics";
import { isAnalyticsGranted } from "@/lib/consent";

/* ─── Types ─── */
export type CTAVariant = "control" | "price_my_connection" | "build_my_network";

export const CTA_VARIANT_LABELS: Record<CTAVariant, string> = {
  control: "Try NaaS Configurator",
  price_my_connection: "Price my connection",
  build_my_network: "Build my network",
};

/**
 * Bulgarian labels for /bg/. DRAFT translation, marked for human review
 * (REVIEW-BG). The control arm matches i18n.ts try_naas_configurator.
 * Analytics keep the variant id, so the experiment data is unaffected.
 */
export const CTA_VARIANT_LABELS_BG: Record<CTAVariant, string> = {
  control: "Пробвай NaaS конфигуратора",
  price_my_connection: "Изчисли цена за връзка",
  build_my_network: "Изгради своята мрежа",
};

const STORAGE_KEY = "ab_cta_variant";
const SESSION_EXPOSED_KEY = "ab_cta_exposed";

/** All possible variants, in order, for deterministic 1/3 bucketing. */
const VARIANTS: CTAVariant[] = ["control", "price_my_connection", "build_my_network"];

/**
 * Deterministic client-side variant assignment.
 * Generates a random UUID on first call and persists it to localStorage.
 * Subsequent calls return the same variant for that browser session.
 *
 * SSR-safe: returns "control" when window/localStorage is unavailable.
 */
export function getCTAVariant(): CTAVariant {
  if (typeof window === "undefined") return "control";

  // A/B bucketing is a non-essential analytics activity: without
  // analytics consent we return the neutral "control" arm and do NOT
  // persist any bucket to localStorage. This keeps the site
  // deterministic (same user, same experience) once consent is granted
  // and avoids setting a tracking key that we would then have to sweep.
  if (!isAnalyticsGranted()) return "control";

  try {
    const stored = localStorage.getItem(STORAGE_KEY) as CTAVariant | null;
    if (stored && VARIANTS.includes(stored)) {
      return stored;
    }

    // Generate a new bucket — use crypto.randomUUID if available, otherwise Math.random
    const uuid =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);

    // Map uuid to a bucket: sum char codes of the UUID and mod 3
    let sum = 0;
    for (let i = 0; i < uuid.length; i++) {
      sum += uuid.charCodeAt(i);
    }
    const variant = VARIANTS[sum % 3];
    localStorage.setItem(STORAGE_KEY, variant);
    return variant;
  } catch {
    // localStorage unavailable (private browsing, security policy, SSR)
    return "control";
  }
}

/**
 * Fire a variant exposure event — idempotent per session.
 * Subsequent calls within the same session are no-ops.
 */
export function trackVariantExposure(variant: CTAVariant): void {
  if (typeof window === "undefined") return;
  if (!isAnalyticsGranted()) return;

  try {
    const alreadyTracked = sessionStorage.getItem(SESSION_EXPOSED_KEY);
    if (alreadyTracked) return;

    trackEvent("ab_variant_exposure", {
      ab_variant: variant,
      ab_test: "hero_cta",
      surface: "hero",
    });

    sessionStorage.setItem(SESSION_EXPOSED_KEY, "1");
  } catch {
    // Silent — analytics should never break UX
  }
}
